import { useEffect, useMemo, useState } from "react";

import FunctionalButton from "components/layout/FunctionalButton";
import BehaviourRuleCard from "components/navigation/mission/BehaviourRuleCard";
import BehaviourRuleDialog from "components/navigation/mission/BehaviourRuleDialog";
import {
    useCreateBehaviourProfileMutation,
    useDeleteBehaviourProfileMutation,
    useGetBehaviourCatalogQuery,
    useGetBehaviourProfileQuery,
    useGetBehaviourProfilesQuery,
    useUpdateBehaviourProfileMutation,
    formatBehaviourApiError,
} from "app/services/behaviourRulesApi";
import {
    buildFailurePolicyRules,
    failureActionTypeFromRule,
    failurePolicyFromRuleAction,
    isFailurePolicyRule,
} from "lib/consts/missionStepFailure";
import {
    buildMissionLightRules,
    isMissionLightsRule,
    lightTypesFromRule,
    missionLightsFromSelection,
} from "lib/consts/missionLights";
import {
    buildMissionAudioRules,
    isMissionAudioRule,
    missionAudioFromRule,
} from "lib/consts/missionAudio";

import { ReactComponent as AddIcon } from "assets/svgs/Plus.svg";
import { ReactComponent as BinIcon } from "assets/svgs/Editor/bin.svg";

const BehaviourProfilesPanel = () => {
    const {
        data: profilesData,
        isLoading: isProfilesLoading,
        isError: isProfilesError,
        refetch: refetchProfiles,
    } = useGetBehaviourProfilesQuery();
    const { data: catalogData, isError: isCatalogError } = useGetBehaviourCatalogQuery();

    const profiles = profilesData?.profiles ?? [];
    const [selectedProfileId, setSelectedProfileId] = useState(null);
    const [showAddProfile, setShowAddProfile] = useState(false);
    const [newProfileName, setNewProfileName] = useState("");
    const [editingRule, setEditingRule] = useState(null);
    const [showRuleDialog, setShowRuleDialog] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const {
        data: profileData,
        isLoading: isProfileLoading,
        refetch: refetchProfile,
    } = useGetBehaviourProfileQuery(selectedProfileId, { skip: !selectedProfileId });

    const [createProfile] = useCreateBehaviourProfileMutation();
    const [updateProfile] = useUpdateBehaviourProfileMutation();
    const [deleteProfile] = useDeleteBehaviourProfileMutation();

    useEffect(() => {
        if (!selectedProfileId && profiles.length > 0) {
            setSelectedProfileId(profiles[0].id);
        }
    }, [profiles, selectedProfileId]);

    const selectedProfile = profileData?.profile;
    const rules = useMemo(() => selectedProfile?.rules ?? [], [selectedProfile]);
    const failurePolicies = useMemo(
        () => selectedProfile?.failure_policies ?? {},
        [selectedProfile],
    );
    const missionLights = useMemo(
        () => selectedProfile?.mission_lights ?? {},
        [selectedProfile],
    );
    const missionAudio = useMemo(
        () => selectedProfile?.mission_audio ?? {},
        [selectedProfile],
    );
    const failurePolicyRules = useMemo(
        () => buildFailurePolicyRules(failurePolicies, catalogData?.failure_policy_actions),
        [failurePolicies, catalogData?.failure_policy_actions],
    );
    const missionLightRules = useMemo(
        () => buildMissionLightRules(missionLights, catalogData?.mission_light_types),
        [missionLights, catalogData?.mission_light_types],
    );
    const missionAudioRules = useMemo(
        () => buildMissionAudioRules(missionAudio),
        [missionAudio],
    );
    const displayRules = useMemo(
        () => [...missionLightRules, ...missionAudioRules, ...failurePolicyRules, ...rules],
        [missionLightRules, missionAudioRules, failurePolicyRules, rules],
    );

    const persistProfile = async (payload) => {
        if (!selectedProfileId) return;
        setBusy(true);
        setError("");
        try {
            await updateProfile({ profileId: selectedProfileId, ...payload }).unwrap();
            await refetchProfile();
            await refetchProfiles();
        } catch (e) {
            setError(formatBehaviourApiError(e, "Failed to save profile."));
        } finally {
            setBusy(false);
        }
    };

    const persistRules = async (nextRules) => {
        await persistProfile({ rules: nextRules });
    };

    const persistFailurePolicies = async (nextPolicies) => {
        await persistProfile({ failure_policies: nextPolicies });
    };

    const persistMissionLights = async (nextLights) => {
        await persistProfile({ mission_lights: nextLights });
    };

    const persistMissionAudio = async (nextAudio) => {
        await persistProfile({ mission_audio: nextAudio });
    };

    const handleCreateProfile = async () => {
        const name = newProfileName.trim();
        if (!name) {
            setError("Profile name is required.");
            return;
        }
        setBusy(true);
        setError("");
        try {
            const result = await createProfile({ name }).unwrap();
            setNewProfileName("");
            setShowAddProfile(false);
            setSelectedProfileId(result.profile.id);
            await refetchProfiles();
        } catch (e) {
            setError(formatBehaviourApiError(e, "Failed to create profile."));
        } finally {
            setBusy(false);
        }
    };

    const handleDeleteProfile = async (profileId) => {
        setBusy(true);
        setError("");
        const remainingProfiles = profiles.filter((profile) => profile.id !== profileId);
        if (selectedProfileId === profileId) {
            setSelectedProfileId(remainingProfiles[0]?.id ?? null);
        }
        try {
            await deleteProfile(profileId).unwrap();
            await refetchProfiles();
        } catch (e) {
            setError(formatBehaviourApiError(e, "Failed to delete profile."));
        } finally {
            setBusy(false);
        }
    };

    const openCreateRule = () => {
        setEditingRule(null);
        setShowRuleDialog(true);
    };

    const openEditRule = (rule) => {
        setEditingRule(rule);
        setShowRuleDialog(true);
    };

    const handleSaveRule = async (ruleDraft) => {
        if (isMissionLightsRule(ruleDraft)) {
            const selected = lightTypesFromRule(ruleDraft);
            if (selected.length === 0) {
                setError("Select at least one light.");
                return;
            }
            await persistMissionLights(
                missionLightsFromSelection(selected, {
                    enabled: ruleDraft.enabled !== false,
                    name: ruleDraft.name,
                }),
            );
            setShowRuleDialog(false);
            setEditingRule(null);
            return;
        }

        if (isMissionAudioRule(ruleDraft)) {
            const trackId = String(ruleDraft.action?.params?.track_id || "").trim();
            if (!trackId) {
                setError("Select an audio track (upload in Payload → Patrol Module settings).");
                return;
            }
            await persistMissionAudio(
                missionAudioFromRule(ruleDraft, {
                    enabled: ruleDraft.enabled !== false,
                }),
            );
            setShowRuleDialog(false);
            setEditingRule(null);
            return;
        }

        if (isFailurePolicyRule(ruleDraft)) {
            const actionType = failureActionTypeFromRule(
                ruleDraft,
                catalogData?.event_sources,
            );
            if (!actionType) {
                setError("Select a mission step failure event.");
                return;
            }
            const policy = failurePolicyFromRuleAction(ruleDraft.action, {
                enabled: ruleDraft.enabled,
            });
            const nextPolicies = { ...failurePolicies };
            nextPolicies[actionType] = policy;
            await persistFailurePolicies(nextPolicies);
            setShowRuleDialog(false);
            setEditingRule(null);
            return;
        }

        const nextRules = [...rules];
        const existingIndex = nextRules.findIndex((rule) => rule.id && rule.id === ruleDraft.id);
        const normalized = {
            ...ruleDraft,
            id: ruleDraft.id || `rule-${Date.now()}`,
        };
        if (existingIndex >= 0) {
            nextRules[existingIndex] = normalized;
        } else {
            nextRules.push(normalized);
        }
        await persistRules(nextRules);
        setShowRuleDialog(false);
        setEditingRule(null);
    };

    const handleDeleteRule = async (rule) => {
        if (isMissionLightsRule(rule)) {
            await persistMissionLights({});
            return;
        }
        if (isMissionAudioRule(rule)) {
            await persistMissionAudio({});
            return;
        }
        if (isFailurePolicyRule(rule)) {
            const actionType = failureActionTypeFromRule(rule, catalogData?.event_sources);
            if (!actionType) return;
            const nextPolicies = { ...failurePolicies };
            delete nextPolicies[actionType];
            await persistFailurePolicies(nextPolicies);
            return;
        }
        await persistRules(rules.filter((item) => item.id !== rule.id));
    };

    const handleToggleRule = async (rule) => {
        if (isMissionLightsRule(rule)) {
            const selected = lightTypesFromRule(rule);
            if (selected.length === 0) return;
            await persistMissionLights(
                missionLightsFromSelection(selected, {
                    enabled: !rule.enabled,
                    name: typeof rule.name === "string" ? rule.name : undefined,
                }),
            );
            return;
        }
        if (isMissionAudioRule(rule)) {
            await persistMissionAudio(
                missionAudioFromRule(rule, { enabled: !rule.enabled }),
            );
            return;
        }
        if (isFailurePolicyRule(rule)) {
            const actionType = failureActionTypeFromRule(rule, catalogData?.event_sources);
            if (!actionType) return;
            const current = failurePolicies[actionType];
            if (!current) return;
            const nextPolicy = { ...current };
            if (rule.enabled) {
                nextPolicy.enabled = false;
            } else {
                delete nextPolicy.enabled;
            }
            await persistFailurePolicies({
                ...failurePolicies,
                [actionType]: nextPolicy,
            });
            return;
        }
        const nextRules = rules.map((item) =>
            item.id === rule.id ? { ...item, enabled: !item.enabled } : item,
        );
        await persistRules(nextRules);
    };

    return (
        <div className="w-full space-y-4 px-2 pb-4">
            <p className="text-h5 text-black40">
                Behaviour profiles contain if-then rules that run only while a mission using that profile is active.
            </p>

            {(isProfilesError || isCatalogError) && (
                <p className="text-h5 text-warning">
                    Failed to reach behaviour server on port 12407.
                </p>
            )}
            {error && <p className="text-h5 text-warning">{error}</p>}

            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <p className="text-h5 font-semibold">Profiles</p>
                    <FunctionalButton
                        icon={<AddIcon className="w-4" />}
                        className="h-6 w-6 rounded-r3 hover:bg-black70"
                        onClick={() => setShowAddProfile(true)}
                        disabled={busy || showAddProfile}
                    />
                    {selectedProfileId && !showAddProfile && (
                        <FunctionalButton
                            icon={<BinIcon className="w-4" />}
                            className="h-6 w-6 rounded-r3 hover:bg-black70"
                            onClick={() => handleDeleteProfile(selectedProfileId)}
                            disabled={busy}
                        />
                    )}
                </div>
                {isProfilesLoading && <p className="text-h5 text-black40">Loading profiles...</p>}
                <div className="flex flex-wrap gap-2">
                    {profiles.map((profile) => {
                        const isSelected = profile.id === selectedProfileId && !showAddProfile;
                        return (
                            <button
                                key={profile.id}
                                type="button"
                                className={`rounded-r5 border px-3 py-2 text-h5 ${
                                    isSelected
                                        ? "border-primary2 bg-primary2 text-white"
                                        : "border-black70 bg-black90 text-black40"
                                }`}
                                onClick={() => {
                                    if (showAddProfile) {
                                        setShowAddProfile(false);
                                        setNewProfileName("");
                                        setError("");
                                    }
                                    setSelectedProfileId(profile.id);
                                }}
                            >
                                {profile.name} ({profile.rule_count})
                            </button>
                        );
                    })}
                </div>
                {showAddProfile ? (
                    <div className="flex gap-2">
                        <input
                            className="min-w-0 flex-1 rounded-r5 border border-black70 bg-black90 px-3 py-2 text-h5"
                            placeholder="New profile name"
                            value={newProfileName}
                            onChange={(e) => setNewProfileName(e.target.value)}
                            autoFocus
                        />
                        <FunctionalButton
                            title="Create"
                            className="h-9 rounded-r5 border border-primary2 bg-primary2 px-3 text-h5"
                            onClick={handleCreateProfile}
                            disabled={busy}
                        />
                        <FunctionalButton
                            title="Cancel"
                            className="h-9 rounded-r5 border border-black70 bg-black90 px-3 text-h5"
                            onClick={() => {
                                setShowAddProfile(false);
                                setNewProfileName("");
                                setError("");
                            }}
                            disabled={busy}
                        />
                    </div>
                ) : null}
            </div>

            {selectedProfileId && !showAddProfile && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <p className="text-h5 font-semibold">
                            Rules for {selectedProfile?.name ?? selectedProfileId}
                        </p>
                        <FunctionalButton
                            title="Add rule"
                            className="h-8 rounded-r5 border border-primary2 bg-primary2 px-3 text-h5"
                            onClick={openCreateRule}
                            disabled={busy || isProfileLoading}
                        />
                    </div>
                    {isProfileLoading && <p className="text-h5 text-black40">Loading rules...</p>}
                    {!isProfileLoading && displayRules.length === 0 && (
                        <p className="text-h5 text-black40">No rules in this profile yet.</p>
                    )}
                    <div className="space-y-2">
                        {displayRules.map((rule) => (
                            <BehaviourRuleCard
                                key={rule.id}
                                rule={rule}
                                catalog={catalogData}
                                onEdit={openEditRule}
                                onDelete={handleDeleteRule}
                                onToggleEnabled={handleToggleRule}
                                isBusy={busy}
                            />
                        ))}
                    </div>
                </div>
            )}

            <BehaviourRuleDialog
                visible={showRuleDialog}
                initialRule={editingRule}
                catalog={catalogData}
                onClose={() => {
                    setShowRuleDialog(false);
                    setEditingRule(null);
                }}
                onSave={handleSaveRule}
                isSaving={busy}
            />
        </div>
    );
};

export default BehaviourProfilesPanel;
